import React from "react";
import { FaPlus, FaTrashAlt } from "react-icons/fa";
import { CustomDropdown, TextInput } from "../atoms";
import {
  VERSION_FIELDS,
  getVersionField,
  getVersionOperator,
  getOperatorsForField,
  isCriterionValueValid,
  isModelField,
} from "../../utils/versionUtils";

let criterionSeq = 0;

/** Yeni (boş) sürüm kriteri üretir — sayfalar temizleme/başlangıç için kullanır. */
export const createVersionCriterion = (overrides = {}) => {
  criterionSeq += 1;
  return {
    id: `version-${criterionSeq}`,
    field: "appVersion",
    operator: "lt",
    value: "",
    ...overrides,
  };
};

/**
 * Sürüm filtresi — birden fazla kriter satırı (VE ile birleşir).
 * Her satır: sürüm alanı + karşılaştırma operatörü + hedef sürüm.
 * Kart modeli alanlarında (ör. "Fincanda Askı Modeli") üçüncü sütun model
 * numarasıdır ve yalnız eşitlik / bildirilmiş operatörleri sunulur.
 *
 * Filtreleme frontend'de yapılır: rapor tüm cihazları döndürür, kriterler
 * `matchesVersionCriteria` ile uygulanır (bkz. utils/versionUtils.js).
 */
const ReportVersionFilter = ({
  criteria = [],
  onChange,
  fields = VERSION_FIELDS,
  maxRows = 5,
  title = "Sürüm Filtresi",
  description = "Sürüm veya kart modeli alanı ve karşılaştırma seçin; birden fazla kriter eklenirse hepsi birlikte uygulanır.",
}) => {
  const fieldOptions = fields.map((field) => ({
    id: field.id,
    label: field.label,
  }));

  const updateCriterion = (id, patch) => {
    onChange(
      criteria.map((criterion) =>
        criterion.id === id ? { ...criterion, ...patch } : criterion
      )
    );
  };

  // Alan değişince operatör/değer yeni alana uymuyorsa sıfırlanır: sürümden
  // modele geçerken "2.9.2" model 2 diye okunmasın, "<" model alanında kalmasın.
  const changeField = (criterion, fieldId) => {
    const patch = { field: fieldId };
    const allowed = getOperatorsForField(fieldId);
    if (!allowed.some((operator) => operator.id === criterion.operator)) {
      patch.operator = "eq";
    }
    if (
      isModelField(getVersionField(fieldId)) !==
      isModelField(getVersionField(criterion.field))
    ) {
      patch.value = "";
    }
    updateCriterion(criterion.id, patch);
  };

  const removeCriterion = (id) => {
    const next = criteria.filter((criterion) => criterion.id !== id);
    onChange(next.length ? next : [createVersionCriterion()]);
  };

  const addCriterion = () => {
    if (criteria.length >= maxRows) return;
    onChange([...criteria, createVersionCriterion()]);
  };

  return (
    <div className="mt-6 space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        <p className="text-[11px] text-gray-400 mt-0.5">{description}</p>
      </div>

      <div className="space-y-3">
        {criteria.map((criterion) => {
          const field = getVersionField(criterion.field);
          const isModel = isModelField(field);
          const operatorOptions = getOperatorsForField(criterion.field);
          const operator = getVersionOperator(criterion.operator);
          const needsValue = operator ? operator.needsValue : true;
          const hasInvalidValue =
            needsValue &&
            criterion.value?.trim?.() &&
            !isCriterionValueValid(criterion.field, criterion.value);

          return (
            <div
              key={criterion.id}
              className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-3 sm:items-end"
            >
              <CustomDropdown
                label="Sürüm Alanı"
                value={fieldOptions.find((opt) => opt.id === criterion.field)}
                onChange={(option) => changeField(criterion, option?.id)}
                options={fieldOptions}
                placeholder="Sürüm alanı seçin..."
                showSearch={false}
              />

              <CustomDropdown
                label="Karşılaştırma"
                value={operatorOptions.find(
                  (opt) => opt.id === criterion.operator
                )}
                onChange={(option) =>
                  updateCriterion(criterion.id, { operator: option?.id })
                }
                options={operatorOptions}
                placeholder="Karşılaştırma seçin..."
                showSearch={false}
              />

              <TextInput
                label={isModel ? "Model No" : "Sürüm"}
                placeholder={field?.placeholder || "örn. 2.9.2"}
                value={criterion.value}
                onChange={(e) =>
                  updateCriterion(criterion.id, { value: e.target.value })
                }
                disabled={!needsValue}
                error={!!hasInvalidValue}
                errorMessage={
                  isModel
                    ? "Model numarası girin (örn. 41)"
                    : "Sürüm en az bir sayı içermeli (örn. 2.9.2)"
                }
                className={!needsValue ? "bg-gray-50 text-gray-400" : ""}
              />

              <button
                type="button"
                onClick={() => removeCriterion(criterion.id)}
                title="Kriteri kaldır"
                aria-label="Kriteri kaldır"
                className="flex items-center justify-center gap-1.5 h-[46px] px-3 rounded-2xl bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors text-xs font-semibold"
              >
                <FaTrashAlt className="w-3 h-3" />
                <span className="sm:hidden">Kaldır</span>
              </button>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={addCriterion}
        disabled={criteria.length >= maxRows}
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors text-xs font-semibold disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
      >
        <FaPlus className="w-3 h-3" />
        Kriter Ekle
      </button>
    </div>
  );
};

export default ReportVersionFilter;
